/**
 * Språkroutning: svenska sidor ligger på rot-URL:er, engelska på /en/*.
 * Kartan används av språkväljaren, LocalizedLink och hreflang-taggarna.
 */
export const SITE_URL = "https://alkoholprovet.se";

/** svensk path -> engelsk path */
export const SV_TO_EN: Record<string, string> = {
  "/": "/en",
  "/practice": "/en/practice",
  "/exam": "/en/exam",
  "/results": "/en/results",
  "/pricing": "/en/pricing",
  "/checkout": "/en/checkout",
  "/login": "/en/login",
  "/dashboard": "/en/dashboard",
  "/faq": "/en/faq",
  "/kontakt": "/en/contact",
  "/egenkontrollparm": "/en/binder",
  "/foretag": "/en/business",
  "/digitala-tjanster": "/en/digital-services",
  "/feedback": "/en/feedback",
  "/ovningsplan": "/en/study-plan",
};

export const EN_TO_SV: Record<string, string> = Object.fromEntries(
  Object.entries(SV_TO_EN).map(([sv, en]) => [en, sv]),
);

export function isEnglishPath(pathname: string | undefined | null): boolean {
  if (!pathname) return false;
  return pathname === "/en" || pathname.startsWith("/en/");
}

/** Översätter en svensk path till engelsk om en engelsk motsvarighet finns. */
export function toEnglishPath(path: string): string {
  const [base, rest] = splitPath(path);
  const en = SV_TO_EN[base];
  return en ? en + rest : path;
}

/** Översätter en engelsk path till svensk motsvarighet. */
export function toSwedishPath(path: string): string {
  const [base, rest] = splitPath(path);
  const sv = EN_TO_SV[base];
  return sv ? sv + rest : path;
}

function splitPath(path: string): [string, string] {
  const i = path.search(/[?#]/);
  if (i === -1) return [path, ""];
  return [path.slice(0, i), path.slice(i)];
}

type MetaTag = Record<string, string | undefined>;
type LinkTag = Record<string, string | undefined>;
type HeadResult = { meta?: MetaTag[]; links?: LinkTag[]; scripts?: unknown[] };
type RouteOptionsLike = { head?: (ctx: never) => HeadResult } & Record<string, unknown>;

function alternateLinks(svPath: string, enPath: string): LinkTag[] {
  const svHref = SITE_URL + (svPath === "/" ? "/" : svPath);
  const enHref = SITE_URL + enPath;
  // React kräver camelCase-propen hrefLang; "hreflang" gav konsolfel på
  // varje sidladdning och renderades inte som attribut.
  return [
    { rel: "alternate", hrefLang: "sv", href: svHref },
    { rel: "alternate", hrefLang: "en", href: enHref },
    { rel: "alternate", hrefLang: "x-default", href: svHref },
  ];
}

/**
 * Lägger hreflang-alternativ på en befintlig svensk sida utan att röra
 * dess titel, description eller canonical.
 */
export function withAlternates<T extends RouteOptionsLike>(options: T, svPath: string): T {
  const enPath = SV_TO_EN[svPath];
  if (!enPath) return options;
  const original = options.head;
  return {
    ...options,
    head: (ctx: never) => {
      const base: HeadResult = original ? original(ctx) : {};
      return { ...base, links: [...(base.links ?? []), ...alternateLinks(svPath, enPath)] };
    },
  };
}

export type EnHeadInput = {
  svPath: string;
  title: string;
  description: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  noindex?: boolean;
  scripts?: unknown[];
};

/**
 * Bygger engelska route-options: samma beteende som den svenska sidan
 * (loader, validateSearch, component) men engelsk head med egen canonical
 * och hreflang.
 */
export function enRouteOptions<T extends RouteOptionsLike>(options: T, en: EnHeadInput): T {
  const enPath = SV_TO_EN[en.svPath];
  const { head: _ignored, ...rest } = options;
  const meta: MetaTag[] = [
    { title: en.title },
    { name: "description", content: en.description },
    { property: "og:title", content: en.ogTitle ?? en.title },
    { property: "og:description", content: en.ogDescription ?? en.description },
    { property: "og:url", content: SITE_URL + enPath },
    { property: "og:locale", content: "en_GB" },
  ];
  if (en.ogImage) {
    meta.push({ property: "og:image", content: en.ogImage });
    meta.push({ name: "twitter:image", content: en.ogImage });
  }
  if (en.noindex) meta.push({ name: "robots", content: "noindex" });

  const links: LinkTag[] = en.noindex
    ? []
    : [{ rel: "canonical", href: SITE_URL + enPath }, ...alternateLinks(en.svPath, enPath)];

  return {
    ...(rest as T),
    head: () => ({ meta, links, ...(en.scripts ? { scripts: en.scripts } : {}) }),
  };
}
